import React, { useState, useEffect } from 'react';
import { Link2, Copy, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import useReportSharing from '../../hooks/useReportSharing';
import { HelpTrigger } from '../../help';
import { isShareableStandardReportType } from '../../types/reports';

interface ShareUrlDisplayProps {
  reportType?: string; // For standard reports
  reportDefinitionId?: number; // For custom reports
  workspaceId?: string;
  organizationId?: string; // For org-level standard reports
  className?: string;
}

const ShareUrlDisplay: React.FC<ShareUrlDisplayProps> = ({
  reportType,
  reportDefinitionId,
  workspaceId,
  organizationId,
  className = ''
}) => {
  const { getStandardReportShareUrl, getOrgReportShareUrl, getCustomReportShareUrl } = useReportSharing();
  const [shareUrl, setShareUrl] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const isStandardReportShareBlocked = Boolean(
    reportType && !isShareableStandardReportType(reportType),
  );

  useEffect(() => {
    if (isStandardReportShareBlocked) {
      setIsLoading(false);
      return undefined;
    }

    let isMounted = true;
    
    const loadShareUrl = async () => {
      try {
        setIsLoading(true);
        let url = '';
        
        if (reportType && organizationId) {
          // Org-level standard report sharing
          const response = await getOrgReportShareUrl(reportType, organizationId);
          url = response.shortUrl;
        } else if (reportType && workspaceId) {
          // Workspace-level standard report sharing
          const response = await getStandardReportShareUrl(reportType, workspaceId);
          url = response.shortUrl;
        } else if (reportDefinitionId) {
          // Custom report - returns object with shortUrl
          const response = await getCustomReportShareUrl(reportDefinitionId, workspaceId);
          url = response.shortUrl;
        }
        
        if (isMounted) {
          setShareUrl(url);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Failed to load share URL:', error);
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    loadShareUrl();
    
    return () => {
      isMounted = false;
    };
  }, [
    reportType,
    reportDefinitionId,
    workspaceId,
    organizationId,
    isStandardReportShareBlocked,
    getStandardReportShareUrl,
    getOrgReportShareUrl,
    getCustomReportShareUrl,
  ]);

  const handleCopy = async () => {
    if (!shareUrl) return;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  if (isStandardReportShareBlocked) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={`flex items-center gap-2 text-sm text-text-tertiary dark:text-text-dark-tertiary ${className}`}>
        <Link2 className="w-4 h-4 animate-pulse" />
        <span className="animate-pulse">Loading share link...</span>
      </div>
    );
  }

  if (!shareUrl) {
    return null;
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Link2 className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
      <span className="text-sm text-text-secondary dark:text-text-dark-tertiary">Public Link:</span>
      <HelpTrigger contentKey="report-sharing" size="sm" />
      <code className="text-xs bg-background-tertiary dark:bg-background-dark-tertiary px-2 py-1 rounded text-text-secondary dark:text-text-dark-secondary truncate max-w-xs">
        {shareUrl}
      </code>
      <button
        onClick={handleCopy}
        className="flex items-center gap-1 px-2 py-1 text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200 transition-colors"
        title="Copy link"
      >
        {copied ? (
          <>
            <Check className="w-4 h-4" />
            <span>Copied!</span>
          </>
        ) : (
          <>
            <Copy className="w-4 h-4" />
            <span>Copy</span>
          </>
        )}
      </button>
    </div>
  );
};

export default ShareUrlDisplay;
